import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, map } from "rxjs";
import { EntityApiService } from "../../../../shared/store/entity-store/entity-api.service";
import { environment } from "../../../../../environments/environment";
import { Dechargement } from "../../models/dechargement.model";
import { safeFirstValueFrom } from "../../../../shared/utils/safe-first-value-from";
import { EtatDechargement } from "../../enums/etat-dechargement.enum";

export interface DechargementSearchParams {
  page?: number;
  pageSize?: number;
  etat?: EtatDechargement | EtatDechargement[];
  search?: string;
  fournisseurIds?: string[];
  minDate?: string;
  maxDate?: string;
}

@Injectable({ providedIn: "root" })
export class DechargementApiService implements EntityApiService<Dechargement> {
  httpClient = inject(HttpClient);

  loadOne(id: string): Observable<Dechargement> {
    return this.httpClient.get<Dechargement>(
      `${environment.api()}/dechargements/id/${id}`,
    );
  }

  create(entity: Dechargement): Observable<Dechargement> {
    return this.httpClient.post<Dechargement>(
      `${environment.api()}/dechargements`,
      entity,
    );
  }

  remove(id: string): Observable<string> {
    return this.httpClient.delete<any>(
      `${environment.api()}/dechargements/id/${id}`,
    ).pipe(map(() => id));
  }

  update(entity: Dechargement): Observable<Dechargement> {
    return this.httpClient.post<Dechargement>(
      `${environment.api()}/dechargements`,
      entity,
    );
  }

  loadAll(): Observable<Dechargement[]> {
    return this.httpClient.get<Dechargement[]>(
      `${environment.api()}/dechargements/all`,
    );
  }

  loadForPlanning(): Observable<any[]> {
    return this.httpClient.get<any[]>(
      `${environment.api()}/dechargements/planning`,
    );
  }

  inProgressSummaryForArticle(
    articleId: string,
  ): Observable<{ quantite: number; nbDechargements: number }> {
    return this.httpClient.get<{ quantite: number; nbDechargements: number }>(
      `${environment.api()}/dechargements/in-progress-summary/article/${articleId}`,
    );
  }

  search(params: DechargementSearchParams = {}): Observable<{ data: Dechargement[]; total: number }> {
    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set("page", params.page.toString());
    if (params.pageSize) httpParams = httpParams.set("pageSize", params.pageSize.toString());
    if (params.search) httpParams = httpParams.set("search", params.search);
    if (params.minDate) httpParams = httpParams.set("minDate", params.minDate);
    if (params.maxDate) httpParams = httpParams.set("maxDate", params.maxDate);
    if (params.fournisseurIds?.length) {
      for (const id of params.fournisseurIds) {
        httpParams = httpParams.append("fournisseurIds", id);
      }
    }
    if (params.etat) {
      const etats = Array.isArray(params.etat) ? params.etat : [params.etat];
      for (const e of etats) {
        httpParams = httpParams.append("etat", e);
      }
    }
    return this.httpClient.get<{ data: Dechargement[]; total: number }>(
      `${environment.api()}/dechargements/search`,
      { params: httpParams },
    );
  }

  async loadForCommandeId(commandeId: string) {
    return await safeFirstValueFrom(
      this.httpClient.get<Dechargement[]>(
        `${environment.api()}/dechargements/commande/id/` + commandeId,
      ),
    );
  }

  async dechargement(entity: Dechargement) {
    return await safeFirstValueFrom(
      this.httpClient.post<Dechargement>(
        `${environment.api()}/dechargements`,
        entity,
      ),
    );
  }

  async end(dechargementId: string) {
    return await safeFirstValueFrom(
      this.httpClient.post(
        `${environment.api()}/dechargements/id/${dechargementId}/etat/${EtatDechargement.FINISHED}`,
        null,
      ),
    );
  }
}
